import React, { useRef, useState, useEffect } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlay, faPause, faVolumeUp, faVolumeMute, faExpand, faDownload } from "@fortawesome/free-solid-svg-icons";

const CustomVideoPlayer = ({ src, poster }) => {
    const videoRef = useRef(null);
    const containerRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    let controlsTimeout = useRef(null);

    const formatTime = (time) => {
        if (isNaN(time)) return "00:00";
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    };

    const togglePlay = () => {
        if (isPlaying) {
            videoRef.current.pause();
        } else {
            videoRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        const current = videoRef.current.currentTime;
        const total = videoRef.current.duration;
        setCurrentTime(current);
        if (total) {
            setProgress((current / total) * 100);
        }
    };

    const handleLoadedMetadata = () => {
        setDuration(videoRef.current.duration);
    };

    const handleProgressChange = (e) => {
        const newTime = (e.target.value / 100) * duration;
        videoRef.current.currentTime = newTime;
        setProgress(e.target.value);
    };
    
    const handleEnded = () => {
        setIsPlaying(false);
        setProgress(0);
        setCurrentTime(0);
        setShowControls(true);
    };

    const toggleMute = () => {
        videoRef.current.muted = !isMuted;
        setIsMuted(!isMuted);
    };

    const toggleFullscreen = () => {
        if (!document.fullscreenElement) {
            if (containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen();
            } else if (containerRef.current.webkitRequestFullscreen) {
                containerRef.current.webkitRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
        }
    };

    // Auto-hide controls when playing and mouse is not moving
    const handleMouseMove = () => {
        setShowControls(true);
        if (isPlaying) {
            clearTimeout(controlsTimeout.current);
            controlsTimeout.current = setTimeout(() => {
                setShowControls(false);
            }, 3000);
        }
    };

    const handleMouseLeave = () => {
        if (isPlaying) {
            setShowControls(false);
        }
    };

    return (
        <div 
            ref={containerRef}
            className="relative w-full rounded-2xl overflow-hidden shadow-inner bg-black aspect-video group/videoplayer flex items-center justify-center font-sans"
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            onDoubleClick={toggleFullscreen}
        >
            <video 
                ref={videoRef} 
                src={src}
                poster={poster}
                className="w-full h-full object-contain cursor-pointer"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                onClick={togglePlay}
                playsInline
            />
            
            {/* Ovelay Big Play Button (when paused) */}
            {!isPlaying && (
                <div 
                    className="absolute inset-0 flex items-center justify-center bg-black/30 backdrop-blur-[2px] transition-all duration-300 pointer-events-none"
                >
                    <div className="w-16 h-16 sm:w-20 sm:h-20 bg-blue-600/90 rounded-full flex items-center justify-center shadow-[0_0_30px_rgba(37,99,235,0.5)] transform scale-100 group-hover/videoplayer:scale-110 transition-transform">
                        <FontAwesomeIcon icon={faPlay} className="text-3xl sm:text-4xl text-white ml-2" />
                    </div>
                </div>
            )}

            {/* Custom Control Bar */}
            <div 
                className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent pt-12 pb-3 px-4 transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}
            >
                {/* Progress Bar */}
                <div className="relative h-1.5 bg-white/30 rounded-full mb-3 cursor-pointer group/progress transition-all hover:h-2">
                    <div 
                        className="absolute top-0 left-0 h-full bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full pointer-events-none"
                        style={{ width: `${progress}%` }}
                    ></div>
                    {/* Range handle dot */}
                    <div 
                        className="absolute top-1/2 -mt-[6px] w-3 h-3 bg-white rounded-full shadow pointer-events-none transform scale-0 group-hover/progress:scale-100 transition-transform"
                        style={{ left: `calc(${progress}% - 6px)` }}
                    ></div>
                    <input 
                        type="range" 
                        min="0" 
                        max="100" 
                        value={progress} 
                        onChange={handleProgressChange}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    />
                </div>

                <div className="flex items-center justify-between text-white">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={togglePlay} 
                            className="hover:text-blue-400 transition-colors focus:outline-none w-6 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className="text-lg" />
                        </button>
                        
                        <button 
                            onClick={toggleMute} 
                            className="hover:text-blue-400 transition-colors focus:outline-none w-6 flex items-center justify-center"
                        >
                            <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
                        </button>

                        <div className="text-xs font-mono opacity-80 tracking-widest hidden sm:block">
                            {formatTime(currentTime)} <span className="opacity-50">/</span> {formatTime(duration)}
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-xs font-mono opacity-80 tracking-widest block sm:hidden">
                            {formatTime(currentTime)}
                        </div>
                        <a 
                            href={src} 
                            download 
                            target="_blank" 
                            rel="noreferrer"
                            className="hover:text-blue-400 transition-colors focus:outline-none flex items-center justify-center p-1"
                            title="Télécharger la vidéo"
                        >
                            <FontAwesomeIcon icon={faDownload} className="text-[14px]" />
                        </a>
                        <button 
                            onClick={toggleFullscreen} 
                            className="hover:text-blue-400 transition-colors focus:outline-none p-1"
                            title="Plein écran"
                        >
                            <FontAwesomeIcon icon={faExpand} className="text-lg" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomVideoPlayer;
